import React, { useEffect, useState, useRef } from 'react';

interface AnimatedDescriptionProps {
  beforeText: string;
  afterText: string;
  hasGuessed: boolean;
  animationDelay?: number;
}

const AnimatedDescription: React.FC<AnimatedDescriptionProps> = ({
  beforeText,
  afterText,
  hasGuessed,
  animationDelay = 300,
}) => {
  const [displayedText, setDisplayedText] = useState(beforeText);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<
    'idle' | 'erasing' | 'typing' | 'complete'
  >('idle');
  const hasAfterText = afterText && afterText !== beforeText;

  const [containerHeight, setContainerHeight] = useState<string>('auto');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasGuessed && hasAfterText && animationPhase === 'idle') {
      // Create a temporary hidden element to measure the final text height
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.width = containerRef.current?.offsetWidth + 'px';
      tempDiv.style.fontSize = '0.75rem'; // Assuming text-xs (12px)
      tempDiv.style.lineHeight = '1.3';

      // Measure the height of the final text
      tempDiv.textContent = afterText;
      document.body.appendChild(tempDiv);
      const afterHeight = Math.min(tempDiv.scrollHeight, 47); // Cap at ~3 lines
      document.body.removeChild(tempDiv);

      // Set the height immediately after guess, before animation starts
      setContainerHeight(`${afterHeight}px`);

      // Start animation after the layout adjusts (in the next frame)
      requestAnimationFrame(() => {
        const startTimer = setTimeout(() => {
          setIsAnimating(true);
          setAnimationPhase('erasing');
        }, animationDelay);

        return () => clearTimeout(startTimer);
      });
    } else if (!hasGuessed) {
      // Reset to auto height when not guessed
      setContainerHeight('auto');
    }
  }, [hasGuessed, hasAfterText, animationPhase, afterText, animationDelay]);

  useEffect(() => {
    if (!hasGuessed || !hasAfterText) {
      setDisplayedText(beforeText);
      setAnimationPhase('idle');
      return;
    }

    // Find common prefix length between the two texts
    let commonPrefixLength = 0;
    const maxLength = Math.min(beforeText.length, afterText.length);
    while (
      commonPrefixLength < maxLength &&
      beforeText[commonPrefixLength] === afterText[commonPrefixLength]
    ) {
      commonPrefixLength++;
    }

    // Common prefix content
    const commonPrefix = beforeText.substring(0, commonPrefixLength);

    // Parts to animate
    const textToErase = beforeText.substring(commonPrefixLength);
    const textToType = afterText.substring(commonPrefixLength);

    // Start animation after delay
    const startTimer = setTimeout(() => {
      setIsAnimating(true);

      if (textToErase.length > 0) {
        // Start erasing
        setAnimationPhase('erasing');

        // Erase one character at a time
        let currentText = beforeText;
        const eraseInterval = setInterval(() => {
          if (currentText.length > commonPrefixLength) {
            currentText = currentText.slice(0, -1);
            setDisplayedText(currentText);
          } else {
            clearInterval(eraseInterval);
            setAnimationPhase('typing');

            // Start typing after a short pause
            setTimeout(() => {
              // Type one character at a time
              let typingText = commonPrefix;
              let charIndex = 0;

              const typeInterval = setInterval(() => {
                if (charIndex < textToType.length) {
                  typingText += textToType[charIndex];
                  setDisplayedText(typingText);
                  charIndex++;
                } else {
                  clearInterval(typeInterval);
                  setAnimationPhase('complete');
                }
              }, 15);
            }, 300);
          }
        }, 20);
      } else {
        // Only need to type, no erasing needed
        setAnimationPhase('typing');

        // Type one character at a time
        let typingText = commonPrefix;
        let charIndex = 0;

        const typeInterval = setInterval(() => {
          if (charIndex < textToType.length) {
            typingText += textToType[charIndex];
            setDisplayedText(typingText);
            charIndex++;
          } else {
            clearInterval(typeInterval);
            setAnimationPhase('complete');
          }
        }, 20);
      }
    }, animationDelay);

    return () => {
      clearTimeout(startTimer);
    };
  }, [hasGuessed, beforeText, afterText, hasAfterText, animationDelay]);

  return (
    <div
      className="smart-typing-container"
      ref={containerRef}
      style={{ height: containerHeight }}
    >
      <span className="typing-text">{displayedText}</span>
      {isAnimating && animationPhase !== 'complete' && (
        <span className="typing-cursor"></span>
      )}
    </div>
  );
};

export default AnimatedDescription;
